import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = { 
  prefillDobFromPinfl?: boolean; 
  onTransactionStart?: (transactionId: string) => void;
  onVerificationStart?: (url: string, transactionId: string) => void;
};

const UserForm: React.FC<Props> = ({ prefillDobFromPinfl, onTransactionStart, onVerificationStart }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    pinfl: '',
    dateOfBirth: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [pinflWarning, setPinflWarning] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Проверка длины ПИНФЛ
    if (name === 'pinfl') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length > 0 && digitsOnly.length < 14) {
        setPinflWarning(`Введено ${digitsOnly.length} из 14 цифр. ПИНФЛ должен содержать 14 цифр.`);
      } else {
        setPinflWarning('');
      }
    }
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(e.target.value);
  };

  const pinflToIsoDate = (pinfl: string) => {
    if (!/^\d{14}$/.test(pinfl)) return '';
    const d = pinfl.slice(1, 3); // позиции 2-3
    const m = pinfl.slice(3, 5); // позиции 4-5
    const yy = pinfl.slice(5, 7); // позиции 6-7
    const yyNum = Number(yy);
    const fullYear = yyNum >= 1 && yyNum <= 40 ? 2000 + yyNum : yyNum >= 41 && yyNum <= 99 ? 1900 + yyNum : 2000;
    const dd = d.padStart(2, '0');
    const mm = m.padStart(2, '0');
    return `${fullYear}-${mm}-${dd}`; // yyyy-mm-dd для input type="date"
  };

  useEffect(() => {
    if (!prefillDobFromPinfl) return;
    const iso = pinflToIsoDate(formData.pinfl);
    if (iso && formData.dateOfBirth !== iso) {
      setFormData(prev => ({ ...prev, dateOfBirth: iso }));
    }
  }, [formData.pinfl, formData.dateOfBirth, prefillDobFromPinfl]);

  const formatDateToDDMMYYYY = (isoDate: string) => {
    // input type="date" -> yyyy-mm-dd
    if (!isoDate) return '';
    const [yyyy, mm, dd] = isoDate.split('-');
    if (!yyyy || !mm || !dd) return '';
    return `${dd}.${mm}.${yyyy}`;
  };

  const handleStartCheck = async () => {
    const baseUrl = process.env.REACT_APP_ABLEID_BASE_URL || '';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const projectId = process.env.REACT_APP_ABLEID_PROJECT_ID || '';
    const secret = process.env.REACT_APP_ABLEID_SECRET || '';
    const hookUrl = process.env.REACT_APP_ABLEID_HOOK_URL || '';

    // Если работаем через бэкенд, фронтовые REACT_APP_ABLEID_* не обязательны
    if (!backendUrl) {
      if (!baseUrl || !projectId || !secret) {
        alert('Отсутствуют настройки AbleID. Укажите REACT_APP_ABLEID_BASE_URL, REACT_APP_ABLEID_PROJECT_ID, REACT_APP_ABLEID_SECRET.');
        return;
      }
    }

    if (!/^\d{14}$/.test(formData.pinfl)) {
      alert('ПИНФЛ должен содержать 14 цифр');
      return;
    }

    const birthDate = formatDateToDDMMYYYY(formData.dateOfBirth);
    if (!birthDate) {
      alert('Выберите корректную дату рождения');
      return;
    }

    const transactionId = `txn_${Date.now()}`;
    if (typeof onTransactionStart === 'function') {
      onTransactionStart(transactionId);
    }
    const body = backendUrl
      ? {
          // server-side start (no secret in client request)
          transactionId,
          pinfl: formData.pinfl,
          birthDate,
          lang: i18n.language
        }
      : {
          // client-side start (dev/local only)
          projectId,
          transactionId,
          secret,
          pinfl: formData.pinfl,
          birthDate,
          lang: i18n.language,
          hooks: hookUrl ? [hookUrl] : []
        };

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await fetch(
        backendUrl ? `${backendUrl}/start` : `${baseUrl}/check/start/second`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(body)
        }
      );

      const data = await res.json();
      const fullFromServer = data?.fullUrl || data?.data?.fullUrl;
      if (!res.ok || !fullFromServer) {
        console.error('AbleID error:', data);
        const errorMessage = data?.message || data?.error || `HTTP ${res.status}`;
        alert(`Ошибка AbleID: ${errorMessage}`);
        return;
      }

      const fullUrl: string = fullFromServer;
      
      // Открываем URL верификации в новой вкладке
      // AbleID сделает редирект на наши страницы после завершения верификации
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
      
      // Если есть callback, вызываем его для совместимости
      if (typeof onVerificationStart === 'function') {
        onVerificationStart(fullUrl, transactionId);
      }
    } catch (e) {
      console.error(e);
      alert('Не удалось создать сессию AbleID. Проверьте настройки и сеть.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleStartCheck();
  };

  return (
    <form className="form-container" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="language">{t('form.language')}:</label>
        <select
          id="language"
          onChange={handleLanguageChange}
          value={i18n.language}
        >
          <option value="en">{t('form.languages.en')}</option>
          <option value="ru">{t('form.languages.ru')}</option>
          <option value="uz">{t('form.languages.uz')}</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="pinfl">{t('form.pinfl')}:</label>
        <input
          type="text"
          id="pinfl"
          name="pinfl"
          value={formData.pinfl}
          onChange={handleInputChange}
          maxLength={14}
          pattern="\d{14}"
        />
        {pinflWarning && (
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#856404'
          }}>
            ⚠️ {pinflWarning}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="dateOfBirth">{t('form.dateOfBirth')}:</label>
        <input
          type="date"
          id="dateOfBirth"
          name="dateOfBirth"
          value={formData.dateOfBirth}
          onChange={handleInputChange}
        />
      </div>

      <button
        className="primary-button"
        type="submit"
        disabled={isLoading}
      >
        {isLoading ? 'Запуск...' : 'Начать проверку'}
      </button>
    </form>
  );
};

export default UserForm;
